import { useEffect, useRef, useState } from 'react';
import { useAgentName } from '../../../../lib/use-agent-name';
import { useTranslation } from '../../../../lib/i18n';
import { composerDir } from '../../../../lib/composer-direction';
import { isImeComposing } from '../../../../lib/ime-composition';
import { ATTACHMENT_ACCEPT } from '../../../../lib/attachments';
import type { Dictation } from '../../../../lib/use-dictation';
import type { MentionAgent } from '../../../../lib/agent-mentions';
import { AudioLines, Mic, Square } from 'lucide-react';
import { Button, IconButton } from '../../ui';
import { useVoiceCallSummary } from '../../../../lib/voice-call-presence-context';
import { ComposerPill } from '../../ComposerPill';
import composerPillStyles from '../../ComposerPill/ComposerPill.module.css';
import { SendIcon } from '../../ComposerPill/icons';
import { AttachMenu, CameraMenuIcon, ContactMenuIcon, FileMenuIcon, LocationMenuIcon, TimeMenuIcon } from '../../ComposerPill/AttachMenu';
import { cameraCaptureSupported } from '../../../../lib/camera-capture';
import { RecordingBar } from '../../ComposerPill/RecordingBar';
import { AgentMentionPanel } from './AgentMentionPanel';

export interface ComposerMentionProps {
  open: boolean;
  agents: MentionAgent[];
  onPick: (agent: MentionAgent) => void;
  onDismiss: () => void;
}

export interface ComposerProps {
  compact: boolean;
  narrow: boolean;
  conversationId: string | null;
  inputText: string;
  onInputChange: (v: string) => void;
  onInputAwakeChange: (awake: boolean) => void;
  onSend: () => void;
  onComposerPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  openFilePicker: () => void;
  openContactPicker: () => void;
  openPlacePicker: () => void;
  openTimePicker: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  startUploads: (files: FileList | File[]) => void;
  dictation: Dictation;
  canSend: boolean;
  showStop: boolean;
  onStop: () => void;
  // Start a live voice call (not dictation — a real-time conversation). Unset →
  // the composer shows no call affordance. The call surface (VoiceCallOverlay) owns
  // mute/settings/end; the composer only launches it via the "Speak" pill.
  onStartCall?: () => void;
  // @-mention an agent: the panel floats above the composer (both variants share
  // the relative wrapper) while a `@` trigger is live in the draft.
  mention?: ComposerMentionProps;
  // Reply preview rendered inside the composer, above the textarea.
  replyPreview?: React.ReactNode;
}

// The full-variant composer is one integrated pill (empty landing AND active
// thread): + attach · text · mic · round send. The tight floating panel (compact)
// stacks the textarea over a compact action bar instead — the textarea would
// otherwise fight three buttons for ~180px and the placeholder would clip.
export function Composer(props: ComposerProps) {
  const { t } = useTranslation();
  const composerAgent = useAgentName();
  const { compact, narrow, conversationId, inputText, onInputChange, onInputAwakeChange, onSend, onComposerPaste, openFilePicker, openContactPicker, openPlacePicker, openTimePicker, fileInputRef, startUploads, dictation, canSend, showStop, onStop, onStartCall, mention, replyPreview } = props;
  // A voice call already in progress (minimized island, or its surface) → don't offer to launch
  // ANOTHER one from the composer: drop the "Speak" launcher so both composer variants show Send.
  const voiceSummary = useVoiceCallSummary();
  const voiceCallUp = !!(voiceSummary?.timing.islandVisible || voiceSummary?.timing.surfaceVisible);
  const effectiveStartCall = voiceCallUp ? undefined : onStartCall;
  const dictateLabel = t('chat.dictate');
  const startCallLabel = t('home.composer.startCall');
  const speakLabel = t('home.composer.speak');

  const mentionOpen = !!mention?.open;
  // Mention-panel keyboard handling lives on the wrapper (capture phase) so both
  // variants' textareas get it without threading keydown props into the pill:
  // Escape dismisses the trigger; Enter picks the top match instead of sending a
  // half-typed mention (no matches → falls through to the textarea's send).
  const onMentionKeyDown = mentionOpen
    ? (e: React.KeyboardEvent) => {
        // The Enter that confirms an IME candidate must not pick a mention either.
        if (e.key === 'Enter' && isImeComposing(e)) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          mention!.onDismiss();
        } else if (e.key === 'Enter' && !e.shiftKey && mention!.agents.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          mention!.onPick(mention!.agents[0]);
        }
      }
    : undefined;
  const mentionPanel = mentionOpen ? <AgentMentionPanel agents={mention!.agents} onPick={mention!.onPick} /> : null;

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={ATTACHMENT_ACCEPT}
      style={{ display: 'none' }}
      onChange={(e) => {
        if (e.target.files) startUploads(e.target.files);
        e.target.value = '';
      }}
    />
  );

  // ── Take Photo ────────────────────────────────────────────────────────────────────────────────
  // A SECOND file input, because `capture` is what makes it the camera and putting it on the shared
  // input would send every "Choose file" to the camera too. Resolved after mount (`useEffect`), not
  // during render: the answer needs `window`, and reading it while rendering makes the server and
  // the first client pass disagree.
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [cameraAvailable, setCameraAvailable] = useState(false);
  useEffect(() => {
    setCameraAvailable(cameraCaptureSupported(typeof window === 'undefined' ? undefined : window));
  }, []);
  const openCamera = () => cameraInputRef.current?.click();
  const hiddenCameraInput = cameraAvailable ? (
    <input
      ref={cameraInputRef}
      type="file"
      // Deliberately NOT `multiple`: a camera returns one shot, and pairing the two makes Android
      // hand back a chooser instead of opening the camera.
      accept="image/*"
      capture="environment"
      style={{ display: 'none' }}
      onChange={(e) => {
        if (e.target.files) startUploads(e.target.files);
        e.target.value = '';
      }}
    />
  ) : null;

  // The attach rows, shared by both composer variants so the two can never offer different options.
  //
  // ⚠️ Both phones lead this list with **Photo Library**, and web deliberately does not have it.
  // `<input type="file">` on a phone browser already opens the OS's own sheet — Photo Library /
  // Take Photo / Choose File on iOS Safari — so "Choose file" ALREADY reaches the photo library in
  // the same number of taps, and a row labelled "Photo Library" that opens a menu asking again is a
  // label that lies. Take Photo is different: `capture` genuinely goes straight to the camera, so
  // the label is honest and it saves the tap. This is a deliberate divergence, not a missing port.
  const attachItems = [
    ...(cameraAvailable ? [{ key: 'photo', label: t('chat.attach.takePhoto'), icon: <CameraMenuIcon />, onSelect: openCamera }] : []),
    { key: 'file', label: t('chat.attach.file'), icon: <FileMenuIcon />, onSelect: openFilePicker },
    { key: 'contact', label: t('chat.attach.contact'), icon: <ContactMenuIcon />, onSelect: openContactPicker },
    { key: 'location', label: t('chat.attach.location'), icon: <LocationMenuIcon />, onSelect: openPlacePicker },
    { key: 'time', label: t('chat.attach.shareTime'), icon: <TimeMenuIcon />, onSelect: openTimePicker },
  ];

  if (!compact) {
    return (
      <div style={{ position: 'relative' }} onKeyDownCapture={onMentionKeyDown}>
        {hiddenFileInput}
        {hiddenCameraInput}
        {mentionPanel}
        <ComposerPill
          value={inputText}
          onChange={onInputChange}
          onInputActiveChange={onInputAwakeChange}
          onSubmit={onSend}
          // Figma 14701:126614 — the Prompt box placeholder reads "Ask {agent}" everywhere, not the
          // longer "Ask {agent} to handle something" (that copy is home.composer.placeholderSymphony's
          // own placeholder, not this shared component's).
          placeholder={t('agents.skills.detail.ask', { maestroAgentName: composerAgent.short })}
          onAttach={openFilePicker}
          attachLabel={t('chat.attach.menu')}
          attachTitle={t('chat.attach.menu')}
          onAttachContact={openContactPicker}
          chooseFileLabel={t('chat.attach.file')}
          contactLabel={t('chat.attach.contact')}
          onTakePhoto={cameraAvailable ? openCamera : undefined}
          takePhotoLabel={t('chat.attach.takePhoto')}
          onAttachLocation={openPlacePicker}
          locationLabel={t('chat.attach.location')}
          onAttachTime={openTimePicker}
          shareTimeLabel={t('chat.attach.shareTime')}
          dictation={dictation}
          dictateLabel={dictateLabel}
          transcribingLabel={t('chat.dictate.transcribing')}
          cancelLabel={t('common.cancel')}
          onStartCall={effectiveStartCall}
          startCallLabel={startCallLabel}
          speakLabel={speakLabel}
          canSend={canSend}
          sendLabel={t('common.send')}
          onPaste={onComposerPaste}
          showStop={showStop}
          onStop={onStop}
          stopLabel={t('common.stop')}
          replyPreview={replyPreview}
          // Full-size chat on desktop → the larger dictation waveform; mobile keeps the small bars.
          recordingLarge={!narrow}
        />
      </div>
    );
  }

  if (dictation.status === 'recording') {
    return (
      <div style={{ position: 'relative' }}>
        {hiddenFileInput}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 20, background: 'var(--ds-surface-sunken)', padding: 12, boxShadow: '0 -4px 18px 0 rgba(var(--ds-black-rgb), 0.1)' }}>
          <RecordingBar
            seconds={dictation.seconds}
            levels={dictation.levels}
            cancelLabel={t('common.cancel')}
            sendLabel={t('common.send')}
            onCancel={dictation.cancel}
            onConfirm={dictation.confirm}
          />
        </div>
      </div>
    );
  }

  return (
    // `position: relative` is also the attach panel's containing block here — the compact variant
    // has no ComposerPill card of its own to hang it on, and this wrapper IS the composer's box, so
    // the panel spans it exactly. (The mention panel above the composer relies on it too.)
    <div style={{ position: 'relative' }} onKeyDownCapture={onMentionKeyDown}>
      {hiddenFileInput}
      {hiddenCameraInput}
      {mentionPanel}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {replyPreview}
        <textarea
          className="orion-input"
          // Bidi: follow the direction of what's being TYPED (first strong char), falling back to the
          // UI language while empty so the placeholder sits naturally (mirrors native #6397/composer-direction.ts —
          // `dir="auto"` resolves an empty field to plain ltr, scrambling a mixed-language placeholder).
          dir={composerDir(inputText)}
          value={inputText}
          onChange={(e) => {
            onInputChange(e.target.value);
            onInputAwakeChange(e.target.value.length > 0);
          }}
          onFocus={() => onInputAwakeChange(true)}
          onBlur={() => onInputAwakeChange(inputText.length > 0)}
          onPaste={onComposerPaste}
          onKeyDown={(e) => {
            // The Enter that confirms an IME (kana/kanji, pinyin, Hangul) candidate is not "send".
            if (isImeComposing(e)) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder={conversationId ? t('chat.composer.addLine') : narrow ? t('agents.skills.detail.ask', { maestroAgentName: composerAgent.short }) : t('home.composer.placeholderSymphony', { agentName: composerAgent.short })}
          rows={1}
          // Grammarly + similar extensions inject attributes before hydration (#425/#423).
          suppressHydrationWarning
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AttachMenu
            triggerLabel={t('chat.attach.menu')}
            title={t('chat.attach.menu')}
            triggerClassName={composerPillStyles.chatRound}
            items={attachItems}
          />
          <div style={{ flex: 1 }} />
          {dictation.supported && (
            <button
              type="button"
              onClick={() => (dictation.status === 'recording' ? dictation.confirm() : dictation.start())}
              aria-label={dictateLabel}
              title={dictateLabel}
              style={{ ...ICON_BTN, borderRadius: 'var(--ds-radius-full)', color: dictation.status !== 'idle' ? 'var(--accent-solid)' : 'rgba(var(--hairline-rgb),0.6)' }}
            >
              {/* Figma "Prompt Box Buttons" (14705:128186): a 1.5px absolute stroke, matching every
                  other composer glyph — this one used to carry the default relative 2.5. */}
              <Mic size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            </button>
          )}
          {showStop ? (
            <IconButton
              variant="contained"
              color="standard"
              size="small"
              onClick={onStop}
              aria-label={t('common.stop')}
              className={composerPillStyles.chatRound}
            >
              {/* Figma "Prompt Box Buttons" (14705:128186) draws Stop as a stroked square outline,
                  not a filled swatch. */}
              <Square size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            </IconButton>
          ) : canSend || !effectiveStartCall ? (
            <IconButton
              variant="contained"
              color="standard"
              size="small"
              type="submit"
              disabled={!canSend}
              aria-label={t('common.send')}
              className={`${composerPillStyles.swapIn} ${composerPillStyles.chatRound}`}
            >
              <SendIcon />
            </IconButton>
          ) : (
            // Empty draft with voice available → the "Speak" pill launches the call (Figma 12835:78977).
            <Button size="small" onClick={effectiveStartCall} title={startCallLabel} className={`${composerPillStyles.swapIn} ${composerPillStyles.chatRound}`} startIcon={<AudioLines size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}>
              {speakLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const ATTACH_MENU: React.CSSProperties = {
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  insetInlineStart: 8,
  zIndex: 30,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: 6,
  minWidth: 200,
  borderRadius: 14,
  background: 'var(--ds-surface-sunken)',
  boxShadow: '0 6px 24px 0 rgba(var(--ds-black-rgb), 0.14)',
};

const ATTACH_MENU_ITEM: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 10px',
  border: 'none',
  borderRadius: 9,
  background: 'transparent',
  color: 'rgba(var(--ink-rgb),0.9)',
  cursor: 'pointer',
  fontSize: 13.5,
  fontWeight: 'var(--sym-weight-semibold)',
  textAlign: 'start',
};

const ICON_BTN: React.CSSProperties = {
  flexShrink: 0,
  padding: '10px',
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--sym-radius-button)',
  color: 'rgba(var(--ink-rgb),0.6)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};


